import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import loadable from '@loadable/component';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
  SCHEMA_TYPE_YOUTUBE
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoneyWithCurrency } from '../../util/currencyHelpers';
import { displayDeliveryPickup, displayDeliveryShipping, displayPrice } from '../../util/configHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { addFavorite, removeFavorite } from '../../ducks/user.duck';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  CountryFlag,
  AvatarSmall,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  trackVisit,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
  createProductSchema,
} from './ListingPage.shared';
import { createBreadcrumbSchema } from '../../util/seo';
import ActionBarMaybe from './ActionBarMaybe';
import DOMPurify from 'dompurify';
import SectionTextMaybe from './SectionTextMaybe';
import SectionVideoMaybe from './SectionVideoMaybe';
import SectionReviews from './SectionReviews';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import ContactSellerButton from './ContactSellerButton';
import ShareModal from './ShareModal';
import FsLightbox from 'fslightbox-react';
import LocationMap from './LocationMap';
import StarRating from './StarRating';
import { ListingCard } from '../../components';

// Import API utilities
import { similarListings as fetchSimilarListings, authorListingsCount as fetchAuthorListingsCount } from '../../util/api';

import css from './ListingPage.module.css';
import contactOwnerIcon from '../../assets/ContactIcon.svg';
import InboxIcon from '../../assets/inboxIcon.svg';
import HeartIcon from '../../assets/HeartIcon.svg';
import ShareIcon from '../../assets/ShareIcon.svg';
import ShieldIcon from '../../assets/Shield.svg';
import MapPinIcon from '../../assets/MapPin.svg';
import SectionAuthorMaybe from './SectionAuthorMaybe.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

// Loadable form components for OrderPanel functionality
const BookingTimeForm = loadable(() =>
  import('../../components/OrderPanel/BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import('../../components/OrderPanel/BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import('../../components/OrderPanel/BookingFixedDurationForm/BookingFixedDurationForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import('../../components/OrderPanel/InquiryWithoutPaymentForm/InquiryWithoutPaymentForm')
);
const ProductOrderForm = loadable(() =>
  import('../../components/OrderPanel/ProductOrderForm/ProductOrderForm')
);

const TODAY = new Date();
const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

// Helper function to format user type
const formatUserType = (userType) => {
  if (!userType) return 'User';

  switch (userType.toLowerCase()) {
    case 'trade_seller':
      return 'Trade Seller';
    case 'private_seller':
      return 'Private Seller';
    default:
      return userType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [countryFlagData, setCountryFlagData] = useState(null);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 1
  });
  const [similarListings, setSimilarListings] = useState([]);
  const [authorListingsCount, setAuthorListingsCount] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Detect when footer or map (when no similar listings) is visible to reposition the WhatsApp button
  useEffect(() => {
    if (!mounted) return;

    const handleScroll = () => {
      const btn = document.getElementById('whatsapp-float-btn');
      if (!btn) return;

      const viewportHeight = window.innerHeight;
      const footerEl = document.getElementById('footer');
      const similarCarsEl = document.getElementById('similar-cars');
      const mapEl = document.getElementById('location');

      // Find the topmost blocking element
      let blockingTop = viewportHeight; // default: nothing blocking

      // Only avoid the map if there are no similar listings below it
      if (mapEl && !similarCarsEl) {
        const mapRect = mapEl.getBoundingClientRect();
        if (mapRect.top < viewportHeight && mapRect.bottom > 0) {
          blockingTop = Math.min(blockingTop, mapRect.top);
        }
      }

      if (footerEl) {
        const footerRect = footerEl.getBoundingClientRect();
        if (footerRect.top < viewportHeight) {
          blockingTop = Math.min(blockingTop, footerRect.top);
        }
      }

      // If a blocking element is visible, push button above it
      if (blockingTop < viewportHeight) {
        const offset = viewportHeight - blockingTop + 12;
        btn.style.bottom = `${offset}px`;
      } else {
        btn.style.bottom = '20px';
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mounted]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    onTrackVisit,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  // Favorites functionality
  const dispatch = useDispatch();
  const userFromStore = useSelector(state => state.user.currentUser);
  const favoriteListingId = new UUID(rawParams.id);

  // Check if listing is in user's favorites
  const favoriteListings = userFromStore?.attributes?.profile?.privateData?.favoriteListings || [];
  const isFavorite = favoriteListings.includes(favoriteListingId.uuid);

  // Handle favorite toggle
  const handleFavoriteClick = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (favoriteLoading) return; // Prevent multiple clicks during loading

    if (!userFromStore) {
      // Redirect to login if not authenticated
      history.push('/signup');
      return;
    }

    setFavoriteLoading(true);

    try {
      if (isFavorite) {
        await dispatch(removeFavorite(favoriteListingId.uuid));
      } else {
        await dispatch(addFavorite(favoriteListingId.uuid));
      }
    } catch (error) {
      console.error('Error in favorite toggle:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Handle share - hybrid approach (native share API for mobile, custom modal for desktop)
  const handleShareClick = async () => {
    const listingUrl = typeof window !== 'undefined' ? window.location.href : '';
    const listingTitle = title || 'Check out this listing';
    const listingDescription = description || '';

    // Check if native share is available (typically mobile devices)
    const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (typeof navigator !== 'undefined' && navigator.share && isMobile) {
      // Use native Web Share API for mobile
      try {
        await navigator.share({
          title: listingTitle,
          text: listingDescription.substring(0, 100) + (listingDescription.length > 100 ? '...' : ''),
          url: listingUrl,
        });
      } catch (error) {
        // User cancelled the share or error occurred
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          // Fallback to modal if native share fails
          setShareModalOpen(true);
        }
      }
    } else {
      // Use custom modal for desktop
      setShareModalOpen(true);
    }
  };

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  // Fetch country flag data from author's profile
  useEffect(() => {
    const fetchCountryFlag = async () => {
      try {
        const { createCountryFlagData } = await import('../../util/countryFlags');
        const authorCountry = currentListing?.author?.attributes?.profile?.publicData?.country_bio;

        if (authorCountry) {
          // Map country names to country codes
          const countryCodeMap = {
            'United-Kingdom': 'GB',
            'United-States': 'US',
            'Germany': 'DE',
            'France': 'FR',
            'Italy': 'IT',
            'Spain': 'ES',
            'Netherlands': 'NL',
            'Belgium': 'BE',
            'Switzerland': 'CH',
            'Austria': 'AT',
            'Portugal': 'PT',
            'Poland': 'PL',
            'Czech-Republic': 'CZ',
            'Denmark': 'DK',
            'Sweden': 'SE',
            'Norway': 'NO',
            'Finland': 'FI',
            'Ireland': 'IE',
            'Australia': 'AU',
            'New-Zealand': 'NZ',
            'Canada': 'CA',
            'Japan': 'JP',
            'South-Korea': 'KR',
            'China': 'CN',
            'India': 'IN',
            'Brazil': 'BR',
            'Mexico': 'MX',
            'Argentina': 'AR',
            'South-Africa': 'ZA',
            'Russia': 'RU',
            'Turkey': 'TR',
            'UAE': 'AE',
            'Saudi-Arabia': 'SA',
            'Singapore': 'SG',
            'Malaysia': 'MY',
            'Thailand': 'TH',
            'Indonesia': 'ID',
            'Philippines': 'PH',
            'Vietnam': 'VN',
          };

          const countryCode = countryCodeMap[authorCountry];
          if (countryCode) {
            const flagData = createCountryFlagData(countryCode);
            setCountryFlagData(flagData);
          }
        }
      } catch (error) {
        console.warn('Error fetching country flag:', error);
      }
    };

    if (currentListing?.author?.attributes?.profile?.publicData?.country_bio) {
      fetchCountryFlag();
    }
  }, [currentListing?.author?.attributes?.profile?.publicData?.country_bio]);

  // Track listing visit (excluding owner visits)
  useEffect(() => {
    if (currentListing && currentListing.id && onTrackVisit) {
      // Only track if the listing is loaded and not viewed by the owner
      const isOwner = currentUser?.id?.uuid === currentListing?.author?.id?.uuid;

      if (!isOwner) {
        // Track the visit
        onTrackVisit(currentListing.id);
      }
    }
  }, [currentListing?.id?.uuid]); // Only re-run if the listing ID changes

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  // Fetch similar listings after publicData is available
  useEffect(() => {
    if (currentListing?.id && publicData) {
      const categoryLevel2 = publicData.categoryLevel2;
      const categoryLevel1 = publicData.categoryLevel1;

      // Check if category2 exists and is not null/empty, otherwise use category1
      const hasValidCategory2 = categoryLevel2 && categoryLevel2.trim() !== '';
      const hasValidCategory1 = categoryLevel1 && categoryLevel1.trim() !== '';

      if (hasValidCategory2 || hasValidCategory1) {
        // Build query parameters
        const params = {
          perPage: 4,
          currentListingId: currentListing.id.uuid,
        };

        // Add category filters - prefer category2 if it's valid, otherwise use category1
        if (hasValidCategory2) {
          params.categoryLevel2 = categoryLevel2;
        } else if (hasValidCategory1) {
          params.categoryLevel1 = categoryLevel1;
        }

        // Fetch similar listings using the API helper
        fetchSimilarListings(params)
          .then(response => {
            const listings = response.data || [];
            const includedEntities = response.included || [];

            // Denormalize listings data - add images and author directly to listings
            // This is required by ListingCard component
            const denormalizedListings = listings.map(listing => {
              const denormalizedListing = { ...listing };

              // Add author data directly to listing
              if (listing.relationships?.author?.data?.id) {
                const authorId = listing.relationships.author.data.id.uuid;
                const author = includedEntities.find(
                  entity => entity.type === 'user' && entity.id.uuid === authorId
                );
                if (author) {
                  denormalizedListing.author = author;
                }
              }

              // Add images directly to listing
              if (listing.relationships?.images?.data) {
                const imageIds = listing.relationships.images.data.map(img => img.id.uuid);
                const images = includedEntities.filter(
                  entity => entity.type === 'image' && imageIds.includes(entity.id.uuid)
                );
                // Sort images to maintain order
                images.sort((a, b) => {
                  const aIndex = imageIds.indexOf(a.id.uuid);
                  const bIndex = imageIds.indexOf(b.id.uuid);
                  return aIndex - bIndex;
                });
                denormalizedListing.images = images;
              }

              return denormalizedListing;
            });

            setSimilarListings(denormalizedListings.slice(0, 3));
          })
          .catch(err => {
            console.error('❌ Error fetching similar listings:', err);
            setSimilarListings([]);
          });
      }
    }
  }, [currentListing?.id?.uuid, publicData?.categoryLevel1, publicData?.categoryLevel2]);

  // Fetch author's listings count
  useEffect(() => {
    const authorId = currentListing?.author?.id?.uuid;
    if (authorId) {
      fetchAuthorListingsCount(authorId)
        .then(response => {
          setAuthorListingsCount(response.count || 0);
        })
        .catch(err => {
          console.error('❌ Error fetching author listings count:', err);
          setAuthorListingsCount(0);
        });
    }
  }, [currentListing?.author?.id?.uuid]);

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  // Generate correctly formatted slug for canonical URL
  const correctSlug = createSlug(currentListing.attributes.title || '');

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType, priceVariants, startTimeInterval } = publicData;

  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;

  // Check if listing is POA (Price on Application)
  const isPOA = publicData?.isPOA;

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
    } else {
      onSubmit(values);
    }
  };

  // OrderPanel logic for forms
  const lineItemUnitType = `line-item/${unitType}`;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const showPriceMissing = isPaymentProcess && !price;
  const showInvalidCurrency = isPaymentProcess && price?.currency !== config.currency;

  const timeZone = currentListing?.attributes?.availabilityPlan?.timezone;
  const isClosed = currentListing?.attributes?.state === LISTING_STATE_CLOSED;

  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  const showInquiryForm = mounted && !isClosed && processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = currentListing?.attributes?.publicData || {};

  const validListingTypes = config.listing.listingTypes;
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Price variations logic
  const hasUniqueVariants = priceVariants => {
    const priceVariantsSlugs = priceVariants?.map(variant =>
      variant.name ? createSlug(variant.name) : 'no-name'
    );
    return new Set(priceVariantsSlugs).size === priceVariants.length;
  };

  const hasValidPriceVariants = priceVariants => {
    const isArray = Array.isArray(priceVariants);
    const hasItems = isArray && priceVariants.length > 0;
    const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
    const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);
    return variantsHaveNames && namesAreUnique;
  };

  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  const getCheapestPriceVariant = (priceVariants = []) => {
    return priceVariants.reduce((cheapest, current) => {
      return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
    }, priceVariants[0]);
  };

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
      isPriceVariationsInUse,
      priceVariants,
      preselectedPriceVariant: null,
      isPublishedListing: currentListing.attributes.state === LISTING_STATE_PUBLISHED,
    }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
      ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
      }
      : {};

  // Get props from restOfProps for OrderPanel functionality
  const monthlyTimeSlots = restOfProps.monthlyTimeSlots || {};
  const timeSlotsForDate = restOfProps.timeSlotsForDate || {};
  const onFetchTimeSlots = restOfProps.onFetchTimeSlots || (() => { });

  const sharedProps = {
    lineItemUnitType,
    onSubmit: handleOrderSubmit,
    price,
    marketplaceCurrency: config.currency,
    listingId: currentListing.id,
    isOwnListing,
    marketplaceName: config.marketplaceName,
    onFetchTransactionLineItems: restOfProps.onFetchTransactionLineItems || (() => { }),
    lineItems: restOfProps.lineItems || [],
    fetchLineItemsInProgress: restOfProps.fetchLineItemsInProgress || false,
    fetchLineItemsError: restOfProps.fetchLineItemsError || null,
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const titleAlreadyHasForSale = title.toLowerCase().includes('for sale');
  const schemaTitle = titleAlreadyHasForSale
    ? title
    : intl.formatMessage(
        { id: 'ListingPage.schemaTitle2' },
        { title, price: formattedPrice, marketplaceName }
      );

  // **FIX FOR CANONICAL URL**: Use the correctly formatted slug (already calculated above for redirect)
  // This ensures schemas and canonical tags use the proper URL format
  const canonicalPathname = `/l/${correctSlug}/${listingId.uuid}`;
  const canonicalUrl = `${config.marketplaceRootURL}${canonicalPathname}${location.search}`;

  // Create breadcrumb trail for SEO with category-based names
  const categoryLevel1 = publicData?.categoryLevel1;
  const categoryLevel2 = publicData?.categoryLevel2;
  const categoryNameMap = {
    'cars-standard-listing': 'Porsche Road Cars',
    'porsche-race-cars-for-sale': 'Porsche Race Cars',
    'parts-listing': 'Porsche Parts',
  };
  const categoryName = categoryNameMap[categoryLevel1] || 'Search';
  const categoryUrl = categoryLevel1
    ? `${config.marketplaceRootURL}/s?pub_categoryLevel1=${categoryLevel1}`
    : `${config.marketplaceRootURL}/s`;

  // Model hub link: map listing's categoryLevel2 (or categoryLevel1 for non-car types) to a hub page
  const cat2HubMap = {
    'porsche_993': { path: '/cars/porsche-993', label: 'Porsche 993' },
    'porsche-964': { path: '/cars/porsche-964', label: 'Porsche 964' },
    'porsche-996-1': { path: '/cars/porsche-996', label: 'Porsche 996' },
    '996-gen-2': { path: '/cars/porsche-996', label: 'Porsche 996' },
    '997-gen1': { path: '/cars/porsche-997', label: 'Porsche 997' },
    '997-gen2': { path: '/cars/porsche-997', label: 'Porsche 997' },
    'porsche-911-g-series': { path: '/cars/porsche-930', label: 'Porsche 911 (G-Series)' },
    'porsche-992': { path: '/cars/porsche-992', label: 'Porsche 992' },
    'cayman-718': { path: '/cars/porsche-cayman', label: 'Porsche Cayman' },
    'cayman-981': { path: '/cars/porsche-cayman', label: 'Porsche Cayman' },
    'porsche-cayman-for-sale': { path: '/cars/porsche-cayman', label: 'Porsche Cayman' },
    'p-718': { path: '/cars/porsche-boxster', label: 'Porsche Boxster' },
    'p-981': { path: '/cars/porsche-boxster', label: 'Porsche Boxster' },
    'p-986': { path: '/cars/porsche-boxster', label: 'Porsche Boxster' },
    'p-987': { path: '/cars/porsche-boxster', label: 'Porsche Boxster' },
    '991-gen1': { path: '/cars/porsche-911-gt3', label: 'Porsche 911 GT3' },
    '991-gen2': { path: '/cars/porsche-911-gt3', label: 'Porsche 911 GT3' },
    'porsche-944-for-sale': { path: '/cars/porsche-944', label: 'Porsche 944' },
    'porsche-356-for-sale': { path: '/cars/porsche-356', label: 'Porsche 356' },
    'porsche-914-for-sale': { path: '/cars/porsche-914', label: 'Porsche 914' },
  };
  const cat1HubMap = {
    'porsche-race-cars-for-sale': { path: '/cars/race-cars', label: 'Porsche Race Cars' },
    'parts-listing': { path: '/parts', label: 'Porsche Parts' },
    'porsche-memorabilia-for-sale': { path: '/memorabilia', label: 'Porsche Memorabilia' },
    'porsche-books-art-for-sale': { path: '/memorabilia', label: 'Porsche Memorabilia' },
    'porsche-models-for-sale': { path: '/memorabilia', label: 'Porsche Memorabilia' },
  };
  const modelHub = cat2HubMap[categoryLevel2] || cat1HubMap[categoryLevel1] || null;

  const breadcrumbs = [
    { name: 'Home', url: config.marketplaceRootURL },
    { name: categoryName, url: categoryUrl },
    { name: title, url: canonicalUrl },
  ];

  // Create Product schema using the utility function (works for all listing types)
  const productSchema = createProductSchema(currentListing, canonicalUrl, marketplaceName, intl);

  // Create WebPage schema
  const webPageSchema = {
    '@type': 'WebPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: schemaTitle,
    description: description,
    isPartOf: {
      '@id': `${config.marketplaceRootURL}#website`,
    },
  };

  // Create BreadcrumbList schema
  const breadcrumbSchema = createBreadcrumbSchema(breadcrumbs, canonicalUrl);

  // Combine all schemas into an array
  const listingSchemas = [webPageSchema, productSchema, breadcrumbSchema].filter(s => s != null);

  // Get first image from listing
  const images = currentListing.images || [];
  const firstImage = images.length > 0 ? images[0] : null;
  const firstImageUrl = firstImage?.attributes?.variants?.['scaled-xlarge']?.url ||
    firstImage?.attributes?.variants?.['default']?.url ||
    null;

  // Prepare lightbox sources (not using useMemo to avoid hooks order issues)
  const lightboxSources = images
    .map(image =>
      image?.attributes?.variants?.['scaled-xlarge']?.url ||
      image?.attributes?.variants?.['scaled-large']?.url ||
      image?.attributes?.variants?.['default']?.url ||
      null
    )
    .filter(url => url !== null && url !== '');

  // Handle navigation tab click
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  // Handle gallery open - always start at first image
  const handleGalleryOpen = () => {
    setLightboxController({
      toggler: !lightboxController.toggler,
      slide: 1 // Always start at first image (1-based indexing)
    });
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={listingSchemas}
      canonicalUrl={canonicalUrl}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.insidePageRootFirstLayer}>
          {/* Owner edit buttons */}
          {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
            <ActionBarMaybe
              className={css.actionBarForLargeLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
              currentUser={currentUser}
            />
          ) : null}
          {mounted && currentListing.id ? (
            <ActionBarMaybe
              className={css.actionBarForLargeLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              currentUser={currentUser}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
              }}
            />
          ) : null}

          {firstImageUrl && (
            <img
              src={firstImageUrl}
              alt={title || 'Listing image'}
              className={css.firstListingImage}
              onClick={handleGalleryOpen}
            />
          )}

          {/* Navigation tabs */}
          <div className={css.navigationTabs}>
            <a
              href="#overview"
              className={`${css.navTab} ${activeTab === 'overview' ? css.navTabActive : ''}`}
              onClick={() => handleTabClick('overview')}
            >
              Overview
            </a>
            <a
              href="#vehicle-details"
              className={`${css.navTab} ${activeTab === 'vehicle-details' ? css.navTabActive : ''}`}
              onClick={() => handleTabClick('vehicle-details')}
            >
              Details
            </a>
            <a
              href="#gallery"
              className={`${css.navTab} ${activeTab === 'gallery' ? css.navTabActive : ''}`}
              onClick={() => handleTabClick('gallery')}
            >
              Gallery
            </a>
            <a
              href="#specification"
              className={`${css.navTab} ${activeTab === 'specification' ? css.navTabActive : ''}`}
              onClick={() => handleTabClick('specification')}
            >
              Specification
            </a>
          </div>

          {/* for overview */}
          <div id="overview">
            <div className={css.overviewContainer}>
              {/* left side title , owner name  , short description */}
              <div className={css.overviewLeftSide}>
                <h1 className={css.listingTitle}>{title}</h1>
                {authorDisplayName && (
                  <div className={css.posterName}>
                    {authorDisplayName}
                  </div>
                )}
                <div className={css.shortDescription}>
                  {publicData?.listing_intro
                    ? publicData.listing_intro
                    : (description.length > 400 ? `${description.substring(0, 400)}...` : description)
                  }
                </div>
              </div>

              {/* right side card Reserved or not , price , contact button , send message , save | share button */}
              <div className={css.overviewRightCard}>
                {publicData?.sold ? (
                  <div className={css.soldText}>Sold</div>
                ) : publicData?.reserved ? (
                  <div className={css.reservedText}>Reserved</div>
                ) : null}

                {/* Price or POA */}
                <div className={css.priceDisplay}>
                  {isPOA ? 'POA' : (price ? formatMoneyWithCurrency(intl, price) : formattedPrice)}
                </div>

                {/* Contact Seller button */}
                <ContactSellerButton
                  ensuredAuthor={ensuredAuthor}
                  contactOwnerIcon={contactOwnerIcon}
                  css={css}
                />

                {/* Send Message button */}
                <button
                  className={css.sendMessageButton}
                  onClick={() => {
                    if (!currentUser) {
                      // Redirect to signup if not authenticated
                      const state = { from: `${location.pathname}${location.search}${location.hash}` };
                      history.push(
                        createResourceLocatorString('SignupPage', routeConfiguration, {}, {}),
                        state
                      );
                    } else if (!isOwnListing) {
                      // Navigate to CheckoutPage
                      onSubmit({});
                    }
                  }}
                  disabled={isOwnListing}
                >
                  <img src={InboxIcon} alt="Message" className={css.buttonIcon} />
                  <span>Send Message</span>
                </button>

                {/* Save and Share buttons */}
                <div className={css.saveShareContainer}>
                  <button
                    className={`${css.saveButton} ${isFavorite ? css.saveButtonActive : ''}`}
                    onClick={handleFavoriteClick}
                    disabled={favoriteLoading}
                    aria-label={
                      favoriteLoading
                        ? 'Loading...'
                        : isFavorite
                          ? 'Remove from favorites'
                          : 'Add to favorites'
                    }
                  >
                    <span className={css.buttonIconText}>
                      <img src={HeartIcon} alt="Save" className={css.buttonIcon} />
                    </span>
                    <span>{favoriteLoading ? 'Loading...' : isFavorite ? 'Unsave' : 'Save'}</span>
                  </button>
                  <button className={css.shareButton} onClick={handleShareClick}>
                    <img src={ShareIcon} alt="Share" className={css.buttonIcon} />
                    <span>Share</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Three images section - shows images 2, 3, and 1 if available */}
            {images.length >= 3 && (
              <div className={css.threeImagesContainer}>
                {/* Left column - second image */}
                {images[1] && (
                  <div className={css.threeImagesLeft} onClick={handleGalleryOpen}>
                    <img
                      src={images[1]?.attributes?.variants?.['scaled-xlarge']?.url || images[1]?.attributes?.variants?.['default']?.url}
                      alt={`${title} - Image 2`}
                    />
                  </div>
                )}

                {/* Right column - third and first images */}
                <div className={css.threeImagesRight}>
                  {images[2] && (
                    <div className={css.threeImagesRightTop} onClick={handleGalleryOpen} >
                      <img
                        src={images[2]?.attributes?.variants?.['scaled-xlarge']?.url || images[2]?.attributes?.variants?.['default']?.url}
                        alt={`${title} - Image 3`}
                      />
                    </div>
                  )}
                  {images[0] && (
                    <div className={css.threeImagesRightBottom} onClick={handleGalleryOpen} >
                      <img
                        src={images[0]?.attributes?.variants?.['scaled-xlarge']?.url || images[0]?.attributes?.variants?.['default']?.url}
                        alt={`${title} - Image 1`}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
          {/* for vehicle in details */}
          <div id="vehicle-details">
            <h2 className={css.vehicleDetailsTitle}>Details</h2>
            {publicData?.richDescription ? (
              <div
                className={css.vehicleDetailsDescription}
                onClick={e => {
                  const link = e.target.closest('a');
                  if (link) {
                    e.preventDefault();
                    let href = link.getAttribute('href') || '';
                    // Strip duplicate protocols (e.g. "https:/https://example.com" or "https://https://example.com")
                    href = href.replace(/^https?:\/?\/?(https?:\/\/)/, '$1');
                    // Fix malformed protocol (e.g. "https//example.com" → "https://example.com")
                    href = href.replace(/^(https?)\/\//, '$1://');
                    // Add protocol if missing
                    if (href && !href.startsWith('http://') && !href.startsWith('https://')) {
                      href = 'https://' + href;
                    }
                    if (href) {
                      window.open(href, '_blank', 'noopener,noreferrer');
                    }
                  }
                }}
                dangerouslySetInnerHTML={{
                  __html: typeof window !== 'undefined'
                    ? DOMPurify.sanitize(publicData.richDescription, {
                        ADD_TAGS: ['iframe'],
                        ADD_ATTR: [
                          'allowfullscreen', 'frameborder', 'autoplay', 'cclanguage',
                          'disablekbcontrols', 'enableiframeapi', 'endtime', 'ivloadpolicy',
                          'loop', 'modestbranding', 'origin', 'playlist', 'rel', 'start',
                          'data-youtube-video', 'target',
                        ],
                      })
                    : publicData.richDescription,
                }}
              />
            ) : (
              <div className={css.vehicleDetailsDescription} style={{ whiteSpace: 'pre-line' }}>{description}</div>
            )}
          </div>
          {/* for gallery */}
          <div id="gallery">
            {images.length >= 3 && (
              <div className={css.galleryContainer}>
                {/* First Row - 3 images */}
                <div className={css.galleryRow}>
                  {/* Left column - single large image (4th or 1st) */}
                  <div className={css.galleryLeftSingle} onClick={handleGalleryOpen}>
                    <img
                      src={images[3]?.attributes?.variants?.['scaled-xlarge']?.url || images[0]?.attributes?.variants?.['scaled-xlarge']?.url || images[3]?.attributes?.variants?.['default']?.url || images[0]?.attributes?.variants?.['default']?.url}
                      alt={`${title} - Image ${images[3] ? '4' : '1'}`}
                    />
                  </div>

                  {/* Right column - two stacked images */}
                  <div className={css.galleryRightStacked}>
                    {/* Top image (5th or 2nd) */}
                    <div className={css.galleryRightTop} onClick={handleGalleryOpen}>
                      <img
                        src={images[4]?.attributes?.variants?.['scaled-xlarge']?.url || images[1]?.attributes?.variants?.['scaled-xlarge']?.url || images[4]?.attributes?.variants?.['default']?.url || images[1]?.attributes?.variants?.['default']?.url}
                        alt={`${title} - Image ${images[4] ? '5' : '2'}`}
                      />
                    </div>
                    {/* Bottom image (6th or 3rd) */}
                    <div className={css.galleryRightBottom} onClick={handleGalleryOpen}>
                      <img
                        src={images[5]?.attributes?.variants?.['scaled-xlarge']?.url || images[2]?.attributes?.variants?.['scaled-xlarge']?.url || images[5]?.attributes?.variants?.['default']?.url || images[2]?.attributes?.variants?.['default']?.url}
                        alt={`${title} - Image ${images[5] ? '6' : '3'}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Second Row - 3 images (reversed layout) */}
                <div className={css.galleryRow2}>
                  {/* Left column - two stacked images */}
                  <div className={css.galleryLeftStacked}>
                    {/* Top image (7th or 1st) */}
                    <div className={css.galleryLeftTop} onClick={handleGalleryOpen}>
                      <img
                        src={images[6]?.attributes?.variants?.['scaled-xlarge']?.url || images[0]?.attributes?.variants?.['scaled-xlarge']?.url || images[6]?.attributes?.variants?.['default']?.url || images[0]?.attributes?.variants?.['default']?.url}
                        alt={`${title} - Image ${images[6] ? '7' : '1'}`}
                      />
                    </div>
                    {/* Bottom image (8th or 2nd) with overlay and image count */}
                    <div className={css.galleryLeftBottom} onClick={handleGalleryOpen}>
                      <img
                        src={images[7]?.attributes?.variants?.['scaled-xlarge']?.url || images[1]?.attributes?.variants?.['scaled-xlarge']?.url || images[7]?.attributes?.variants?.['default']?.url || images[1]?.attributes?.variants?.['default']?.url}
                        alt={`${title} - Image ${images[7] ? '8' : '2'}`}
                      />
                      <div className={css.galleryOverlay}>
                        {/* <span className={css.galleryImageCount}>+{images.length}</span> */}
                        <span className={css.viewAllButton}>+ View All</span>
                      </div>
                    </div>
                  </div>

                  {/* Right column - single large image (9th or 3rd) */}
                  <div className={css.galleryRightSingle} onClick={handleGalleryOpen}>
                    <img
                      src={images[8]?.attributes?.variants?.['scaled-xlarge']?.url || images[2]?.attributes?.variants?.['scaled-xlarge']?.url || images[8]?.attributes?.variants?.['default']?.url || images[2]?.attributes?.variants?.['default']?.url}
                      alt={`${title} - Image ${images[8] ? '9' : '3'}`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* for specification */}
          <div id="specification">
            <h2 className={css.specificationTitle}>Specification</h2>
            {(() => {
              // Separate video fields from regular fields
              const videoFields = [];

              // Filter and prepare all fields with values (matching SectionDetailsMaybe logic)
              const fieldsWithValues = listingConfig.listingFields
                .map((fieldConfig) => {
                  const { key, schemaType, enumOptions, showConfig = {} } = fieldConfig;
                  const { isDetail, label } = showConfig;

                  // Only show fields marked as detail
                  if (!isDetail) return null;

                  // Exclude "Listing Highlights" (listing_intro) from specification
                  if (key === 'listing_intro') return null;

                  const publicDataValue = publicData?.[key];
                  const metadataValue = metadata?.[key];
                  const value = publicDataValue !== undefined ? publicDataValue : metadataValue;

                  // Skip if no value exists
                  if (typeof value === 'undefined') return null;

                  // If it's a YouTube video field, collect it separately
                  if (schemaType === SCHEMA_TYPE_YOUTUBE) {
                    videoFields.push({
                      key: key,
                      videoUrl: value,
                      heading: label
                    });
                    return null; // Don't include in specification table
                  }

                  // Format the value based on field type
                  let displayValue = value;

                  if (schemaType === 'enum') {
                    // For enum fields, show the label
                    const option = enumOptions?.find(opt => `${opt.option}` === `${value}`);
                    displayValue = option?.label || value;
                  } else if (schemaType === 'boolean') {
                    displayValue = value
                      ? intl.formatMessage({ id: 'SearchPage.detailYes' })
                      : intl.formatMessage({ id: 'SearchPage.detailNo' });
                  } else if (schemaType === 'long') {
                    // Check if this is a field that should NOT have comma formatting
                    const keyLower = key.toLowerCase();
                    const isYearField = keyLower.includes('year');
                    const isPhoneField = keyLower.includes('phone') || keyLower.includes('mobile') || keyLower.includes('contact');

                    if (isYearField || isPhoneField) {
                      // Display without commas (e.g., "2020", "1234567890")
                      displayValue = value;
                    } else {
                      // Display other numeric fields with commas (e.g., "10,000")
                      displayValue = typeof value === 'number' ? value.toLocaleString('en-US') : value;
                    }
                  }

                  return {
                    key: key,
                    label: label, // This is the field label like "Registration year", "Make", etc.
                    value: displayValue, // This is the actual value like "2004", "Toyota", etc.
                  };
                })
                .filter(field => field !== null);

              // Split fields into two columns for large viewport
              const midPoint = Math.ceil(fieldsWithValues.length / 2);
              const firstColumn = fieldsWithValues.slice(0, midPoint);
              const secondColumn = fieldsWithValues.slice(midPoint);

              return (
                <>
                  {/* Specification table with columns */}
                  <div className={css.specificationFields}>
                    {/* First column */}
                    {firstColumn.length > 0 && (
                      <ul className={css.specificationList}>
                        {firstColumn.map(field => (
                          <li key={field.key} className={css.specificationItem}>
                            <span className={css.specificationLabel}>{field.label}</span>
                            <span className={css.specificationValue}>{field.value}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Second column (only for large viewport) */}
                    {secondColumn.length > 0 && (
                      <ul className={css.specificationList}>
                        {secondColumn.map(field => (
                          <li key={field.key} className={css.specificationItem}>
                            <span className={css.specificationLabel}>{field.label}</span>
                            <span className={css.specificationValue}>{field.value}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Render video fields at the end - full width outside of columns */}
                  {videoFields.length > 0 && (
                    <div className={css.specificationVideoWrapper}>
                      {videoFields.map(videoField => (
                        <SectionVideoMaybe
                          key={videoField.key}
                          videoUrl={videoField.videoUrl}
                          heading={videoField.heading}
                        />
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          {/* seller information */}
          <div id="seller-information" className={css.sellerInformationSection}>

            {/* First row - Profile image, name, country, type, location */}
            <div className={css.sellerInfoFirstRow}>
              {/* Left side - Profile image */}
              <div className={css.sellerProfileImageContainer}>
                <AvatarSmall user={ensuredAuthor} className={css.sellerProfileImage} />
              </div>

              {/* Right side - Name, country flag, seller type, location */}
              <div className={css.sellerInfoDetails}>
                {/* Name + Country Flag */}
                <div className={css.sellerNameWithFlag}>
                  <span className={css.sellerName}>{authorDisplayName}</span>
                  {countryFlagData && (
                    <CountryFlag
                      className={css.sellerCountryFlag}
                      flagData={countryFlagData}
                      size="large"
                    />
                  )}
                </div>

                {/* Seller Type and Location - Side by side */}
                <div className={css.sellerTypeLocationRow}>
                  {/* Seller Type */}
                  <div className={css.sellerType}>
                    <img src={ShieldIcon} alt="Seller Type" className={css.sellerTypeIcon} />
                    <span>{formatUserType(ensuredAuthor?.attributes?.profile?.publicData?.userType)}</span>
                  </div>

                  {/* Location - Using author's country from profile */}
                  {ensuredAuthor?.attributes?.profile?.publicData?.country_bio && (
                    <div className={css.sellerLocation}>
                      <img src={MapPinIcon} alt="Location" className={css.sellerLocationIcon} />
                      <span>{ensuredAuthor.attributes.profile.publicData.country_bio.replace(/-/g, ' ')}</span>
                    </div>
                  )}
                </div>

                {/* Review rating and count - 5-star system */}
                {/* <div className={css.sellerReviewsContainer}>
                  <StarRating
                    rating={reviews.length > 0 ? reviews.reduce((sum, review) => sum + review.attributes.rating, 0) / reviews.length : 0}
                    reviewCount={reviews.length}
                    showCount={true}
                  />
                </div> */}
              </div>
            </div>

            {/* Company Information Section - Only for trade_seller */}
            {ensuredAuthor?.attributes?.profile?.publicData?.userType === 'trade_seller' &&
              (ensuredAuthor?.attributes?.profile?.publicData?.companyLogo ||
               ensuredAuthor?.attributes?.profile?.publicData?.company_name) && (() => {
                // Country code mapping
                const countryCodeMap = {
                  'United-Kingdom': '44', 'Albania': '355', 'Andorra': '376', 'Argentina': '54',
                  'Australia': '61', 'Austria': '43', 'Bahrain': '973', 'Belgium': '32',
                  'Brazil': '55', 'Brunei': '673', 'Bulgaria': '359', 'Canada': '1',
                  'Croatia': '385', 'Cyprus': '357', 'Czechia': '420', 'Czech-Republic': '420',
                  'Denmark': '45', 'Egypt': '20', 'Estonia': '372', 'Finland': '358',
                  'France': '33', 'Germany': '49', 'Gibraltar': '350', 'Greece': '30',
                  'Guernsey': '44', 'Hong-Kong-SAR-China': '852', 'Hong-Kong': '852',
                  'Hungary': '36', 'Ireland': '353', 'Isle-of-Man': '44', 'Italy': '39',
                  'Japan': '81', 'Jersey': '44', 'Kuwait': '965', 'Latvia': '371',
                  'Lithuania': '370', 'Luxembourg': '352', 'Malaysia': '60', 'Malta': '356',
                  'Monaco': '377', 'Montenegro': '382', 'Namibia': '264', 'Netherlands': '31',
                  'New-Zealand': '64', 'North-Macedonia': '389', 'Norway': '47', 'Peru': '51',
                  'Poland': '48', 'Portugal': '351', 'Romania': '40', 'Serbia': '381',
                  'Singapore': '65', 'Slovakia': '421', 'Slovenia': '386', 'South-Africa': '27',
                  'Spain': '34', 'Sweden': '46', 'Switzerland': '41', 'Syria': '963',
                  'Turkey': '90', 'Ukraine': '380', 'United-Arab-Emirates': '971',
                  'United-States': '1', 'Uruguay': '598',
                  // Additional countries
                  'Bosnia-and-Herzegovina': '387', 'China': '86', 'Chile': '56',
                  'Colombia': '57', 'Iceland': '354', 'India': '91', 'Indonesia': '62',
                  'Israel': '972', 'Jordan': '962', 'Kenya': '254', 'Lebanon': '961',
                  'Mexico': '52', 'Morocco': '212', 'Nigeria': '234', 'Oman': '968',
                  'Pakistan': '92', 'Philippines': '63', 'Qatar': '974', 'Russia': '7',
                  'Saudi-Arabia': '966', 'South-Korea': '82', 'Sri-Lanka': '94',
                  'Taiwan': '886', 'Thailand': '66', 'Tunisia': '216', 'Venezuela': '58',
                  'Vietnam': '84', 'Algeria': '213', 'Bangladesh': '880', 'Nepal': '977',
                };

                // Get phone number and country
                const phoneNumber = ensuredAuthor?.attributes?.profile?.publicData?.phone_number;
                const countryName = ensuredAuthor?.attributes?.profile?.publicData?.country_bio || '';

                // Format phone number with country code
                let formattedPhone = '';
                let telLink = '';
                if (phoneNumber) {
                  const phoneStr = String(phoneNumber).replace(/,/g, '');
                  const countryCode = countryCodeMap[countryName] || '';

                  if (countryCode && !phoneStr.startsWith('+')) {
                    formattedPhone = `+${countryCode} ${phoneStr}`;
                    telLink = `+${countryCode}${phoneStr}`;
                  } else if (!phoneStr.startsWith('+') && !phoneStr.startsWith('0')) {
                    formattedPhone = `+${phoneStr}`;
                    telLink = `+${phoneStr}`;
                  } else {
                    formattedPhone = phoneStr;
                    telLink = phoneStr;
                  }
                  // Clean tel link
                  telLink = telLink.replace(/[\s\-()]/g, '');
                }

                return (
              <div className={css.companyInfoSection}>
                {/* Company Logo */}
                {ensuredAuthor?.attributes?.profile?.publicData?.companyLogo && (
                  <div className={css.companyLogoContainer}>
                    <img
                      src={ensuredAuthor.attributes.profile.publicData.companyLogo}
                      alt={ensuredAuthor?.attributes?.profile?.publicData?.company_name || 'Company logo'}
                      className={css.companyLogoImg}
                    />
                  </div>
                )}

                {/* Company Details */}
                <div className={css.companyDetailsContainer}>
                  {/* Company Name */}
                  {ensuredAuthor?.attributes?.profile?.publicData?.company_name && (
                    <div className={css.companyNameContainer}>
                      <svg className={css.companyBuildingIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M3 21h18M3 10h18M5 6l7-3 7 3M6 10v11M10 10v11M14 10v11M18 10v11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className={css.companyNameText}>
                        {ensuredAuthor.attributes.profile.publicData.company_name}
                      </span>
                    </div>
                  )}

                  {/* Company Website */}
                  {ensuredAuthor?.attributes?.profile?.publicData?.website && (
                    <div className={css.companyWebsiteContainer}>
                      <svg className={css.companyWebsiteIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
                      </svg>
                      <a
                        href={ensuredAuthor.attributes.profile.publicData.website.startsWith('http')
                          ? ensuredAuthor.attributes.profile.publicData.website
                          : `https://${ensuredAuthor.attributes.profile.publicData.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={css.companyWebsiteLink}
                      >
                        {ensuredAuthor.attributes.profile.publicData.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </a>
                    </div>
                  )}

                  {/* Company Phone Number */}
                  {formattedPhone && (
                    <div className={css.companyPhoneContainer}>
                      <svg className={css.companyPhoneIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" fill="currentColor"/>
                      </svg>
                      <a
                        href={`tel:${telLink}`}
                        className={css.companyPhoneLink}
                      >
                        {formattedPhone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
                );
              })()
            }

            {/* Second row - Bio */}
            {ensuredAuthor?.attributes?.profile?.bio && (
              <div className={css.sellerBio}>
                {ensuredAuthor.attributes.profile.bio}
              </div>
            )}

            {/* Third row - View Profile button */}
            <div className={css.viewProfileButtonContainer}>
              <NamedLink
                name="ProfilePage"
                params={{ id: ensuredAuthor.id.uuid }}
                className={css.viewProfileButton}
              >
                View profile →
              </NamedLink>
            </div>

            {/* Fourth row - View All Listings button */}
            <div className={css.viewAllListingsButtonContainer}>
              <NamedLink
                name="SearchPage"
                to={{
                  search: `?authorId=${ensuredAuthor.id.uuid}`,
                }}
                className={css.viewAllListingsButton}
              >
                {authorListingsCount > 0
                  ? `View ${authorListingsCount - 1} More Listing${authorListingsCount - 1 !== 1 ? 's' : ''}`
                  : 'View all listings'
                }
              </NamedLink>
            </div>
          </div>


          {/* Location map */}
          <div id="location">
            <h2 className={css.locationTitle}>Location</h2>
            <LocationMap
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />
          </div>

          {/* similar cars - only show if there are listings */}
          {similarListings.length > 0 && (
            <div id="similar-cars">
              <h2 className={css.similarCarsTitle}>Similar Listing</h2>
              <div className={css.similarCarsGrid}>
                {similarListings.map(listing => (
                  <div key={listing.id.uuid} className={css.similarCarCard}>
                    <ListingCard listing={listing} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Model hub link */}
          {modelHub && (
            <div className={css.modelHubBar}>
              <span className={css.modelHubBarText}>Looking for more {modelHub.label} listings?</span>
              <a href={modelHub.path} className={css.modelHubBarLink}>
                Browse all {modelHub.label} listings &rarr;
              </a>
            </div>
          )}

        </div>
      </LayoutSingleColumn>

      {/* Fullscreen Gallery with fsLightbox */}
      {lightboxSources.length > 0 && (
        <FsLightbox
          toggler={lightboxController.toggler}
          sources={lightboxSources}
          slide={lightboxController.slide}
          showThumbsOnMount={true}
        />
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        listingUrl={typeof window !== 'undefined' ? window.location.href : ''}
        listingTitle={title}
        listingDescription={description}
      />

      {/* WhatsApp Fixed Bottom Button — commented out per client request */}
      {/* <a
        href={`https://wa.me/447511915911?text=${encodeURIComponent(`Hi, I'm interested in this listing: ${title}\n${typeof window !== 'undefined' ? window.location.href : ''}`)}`}
        target="_blank"
        rel="noopener noreferrer"
        className={css.whatsappFixedButton}
        id="whatsapp-float-btn"
      >
        <svg className={css.whatsappIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        WhatsApp
      </a> */}
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onTrackVisit: listingId => dispatch(trackVisit(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
